// src/components/Pentests/tabs/Dashboards.js
//
// Phase 2 (2026-05-14): per-PT Dashboards tab. Lays out 4 KPI tiles, a
// 5-axis spider chart of finding categories, a severity pie, and a list of
// the top 5 critical open findings. The same rollup rule as the rest of
// the API surface applies: when this PT is a parent with at least one
// retest, the numbers below come from the most recent retest (excluding
// already-verified findings) - same numbers the page header badges show.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity, AlertCircle, ArrowDownRight, ArrowUpRight, ArrowRight,
  Bug, CheckCircle2, Layers, Target, Sparkles,
} from 'lucide-react';
import { pentestsAPI } from '../../../services/api';
import { SeverityPie, SpiderChart } from '../../common/Charts';

const SEVERITY_BADGE = {
  info: 'bg-slate-100 text-slate-700 border-slate-300',
  low: 'bg-blue-100 text-blue-700 border-blue-300',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  critical: 'bg-red-100 text-red-700 border-red-300',
};

const GRADE_PILL = {
  A: 'text-emerald-700',
  B: 'text-emerald-600',
  C: 'text-yellow-700',
  D: 'text-orange-700',
  F: 'text-red-700',
};

const CATEGORY_LABELS = {
  access_control: 'Access Control',
  injection: 'Injection',
  logging_monitoring_detection: 'Logging / Detection',
  cryptographic: 'Cryptographic',
  security_misconfiguration: 'Misconfiguration',
};

function KPIBox({ label, value, sublabel, icon: Icon, accent = 'green' }) {
  const accentMap = {
    green: 'text-wasabi-700 border-wasabi-300 bg-shinobi-green/5',
    red: 'text-rose-700 border-rose-300 bg-rose-500/5',
    amber: 'text-amber-700 border-amber-300 bg-amber-500/5',
    slate: 'text-slate-700 border-slate-300 bg-slate-500/5',
  };
  const cls = accentMap[accent] || accentMap.green;
  return (
    <div className={`rounded-2xl border p-4 ${cls.split(' ').slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 ${cls.split(' ')[0]}`} />}
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
          {label}
        </span>
      </div>
      <div className={`text-3xl font-bold tabular-nums ${cls.split(' ')[0]}`}>{value}</div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}

export default function Dashboards() {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await pentestsAPI.dashboard(pentestId);
      setData(resp);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => { fetch(); }, [fetch]);

  const spiderData = useMemo(() => {
    if (!data?.spider) return [];
    return [
      { category: CATEGORY_LABELS.access_control, value: data.spider.access_control || 0 },
      { category: CATEGORY_LABELS.injection, value: data.spider.injection || 0 },
      { category: CATEGORY_LABELS.logging_monitoring_detection, value: data.spider.logging_monitoring_detection || 0 },
      { category: CATEGORY_LABELS.cryptographic, value: data.spider.cryptographic || 0 },
      { category: CATEGORY_LABELS.security_misconfiguration, value: data.spider.security_misconfiguration || 0 },
    ];
  }, [data]);

  const severityPieData = useMemo(() => {
    if (!data?.severity_pie) return [];
    return ['critical', 'high', 'medium', 'low', 'info']
      .map((k) => ({ name: k, value: data.severity_pie[k] || 0 }))
      .filter((d) => d.value > 0);
  }, [data]);

  const uncategorized = data?.spider?.uncategorized ?? 0;

  if (loading) return <div className="text-slate-500">Loading dashboard...</div>;
  if (error) return <div className="text-red-600 text-sm">Error: {error}</div>;
  if (!data) return null;

  const k = data.kpis;
  const gradeColor = GRADE_PILL[k.grade] || 'text-slate-700';
  const deltaIcon = k.grade_delta > 0 ? ArrowUpRight : k.grade_delta < 0 ? ArrowDownRight : null;
  const deltaCls = k.grade_delta > 0
    ? 'text-emerald-700'
    : k.grade_delta < 0
      ? 'text-rose-700'
      : 'text-slate-500';

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KPIBox
          label="Total findings"
          value={k.total_findings}
          sublabel={k.false_positives > 0 ? `${k.false_positives} marked FP` : null}
          icon={Bug}
          accent="slate"
        />
        <KPIBox
          label="Open"
          value={k.open}
          sublabel={data.is_retest ? 'on this retest' : 'still active'}
          icon={AlertCircle}
          accent="red"
        />
        <KPIBox
          label="Verified"
          value={k.verified}
          sublabel={k.wont_fix > 0 ? `${k.wont_fix} won't fix` : null}
          icon={CheckCircle2}
          accent="green"
        />
        <div className="rounded-2xl border p-4 border-wasabi-300 bg-shinobi-green/5">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-shinobi-green" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Grade
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <div className={`text-4xl font-bold tabular-nums ${gradeColor}`}>{k.grade}</div>
            <div className="text-xs text-slate-500">
              {Number(k.overall_score).toFixed(2)} / 5.00
            </div>
          </div>
          {k.original_grade && k.grade_delta !== 0 && deltaIcon && (
            <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${deltaCls}`}>
              {(() => { const Icon = deltaIcon; return <Icon className="w-3 h-3" />; })()}
              from {k.original_grade} to {k.grade}
            </div>
          )}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-shinobi-green" />
            <h3 className="text-sm font-semibold text-slate-900">Findings by category</h3>
            {uncategorized > 0 && (
              <span
                className="ml-auto text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-100 text-amber-700 border-amber-300"
                title={`${uncategorized} finding(s) not yet classified by Wasabi Shinobi AI. The periodic catch-up job runs every 15 minutes.`}
              >
                <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1" />
                {uncategorized} uncategorized
              </span>
            )}
          </div>
          {spiderData.every((d) => d.value === 0) ? (
            <div className="text-sm text-slate-500 italic text-center py-12">
              No classified findings yet.
              {uncategorized > 0 && ' The AI classifier is still working through this PT.'}
            </div>
          ) : (
            <SpiderChart data={spiderData} />
          )}
        </div>

        <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Bug className="w-4 h-4 text-shinobi-green" />
            <h3 className="text-sm font-semibold text-slate-900">Severity distribution</h3>
          </div>
          {severityPieData.length === 0 ? (
            <div className="text-sm text-slate-500 italic text-center py-12">
              No open findings.
            </div>
          ) : (
            <SeverityPie data={severityPieData} />
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-shinobi-green" />
          <h3 className="text-sm font-semibold text-slate-900">Top 5 critical open findings</h3>
        </div>
        {data.top_5_critical.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No open findings.</div>
        ) : (
          <ul className="divide-y divide-cyber-300/20">
            {data.top_5_critical.map((f) => {
              const sev = f.severity || 'info';
              const cls = SEVERITY_BADGE[sev] || SEVERITY_BADGE.info;
              return (
                <li key={f.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/pentests/${pentestId}/findings?focus=${f.id}`)}
                    className="w-full text-left px-2 py-2 flex items-center gap-3 hover:bg-cyber-200/30 transition group rounded"
                  >
                    <span className="font-mono text-xs text-slate-500 shrink-0 w-12">#{f.reference_number}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${cls} shrink-0`}>
                      {sev}
                    </span>
                    <span className="flex-1 text-sm text-slate-800 truncate">{f.title}</span>
                    {f.cvss_score != null && (
                      <span className="font-mono text-xs text-slate-500 shrink-0">CVSS {Number(f.cvss_score).toFixed(1)}</span>
                    )}
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-shinobi-green transition shrink-0" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
